import { useMutation, useQueryClient } from '@tanstack/react-query'
import { BookOpen, ImagePlus } from 'lucide-react'
import { FormError } from '@/components/FormError'
import { PickOne } from '@/components/PickOne'
import { albumPhotoUrl, organize, useAlbums } from '@/lib/albums'
import { photoUrl } from '@/lib/photos'
import { sendRecipes, useBooks } from '@/lib/recipes'

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

// Sending picked photos to an album: one tap on the album, or "No album" to take them out of the one they're in, or a
// new album. A photo is in one album at a time, so sending it moves it there; who already sees it still does.
export function SendPhotos({
  open,
  onOpenChange,
  familyId,
  photos,
  from,
  inAlbum,
  onSent,
  onNewAlbum,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  familyId: string
  photos: string[]
  // The album they're being sent from, left off the list.
  from?: string
  // Whether any of them is in an album, so "No album" means something.
  inAlbum: boolean
  onSent: (albumId: string | null) => void
  onNewAlbum: () => void
}) {
  const queryClient = useQueryClient()
  const albums = useAlbums(familyId)
  const send = useMutation({
    mutationFn: async (albumId: string | null) => {
      await organize(familyId, photos, { albumId })
      return albumId
    },
    onSuccess: async (albumId) => {
      for (const key of ['album', 'albums', 'photo', 'found-photos', 'stream', 'person', 'home']) {
        await queryClient.invalidateQueries({ queryKey: [key] })
      }
      onSent(albumId)
    },
  })
  const n = photos.length
  return (
    <PickOne
      open={open}
      onOpenChange={onOpenChange}
      title={`Send ${n === 1 ? 'the photo' : `${n} photos`} to which album?`}
      description="Everyone who sees them now still will. The album adds the people it’s shared with."
      icon={ImagePlus}
      choices={[
        ...(inAlbum ? [{ id: '', title: 'No album', detail: 'Take them out of their album', cover: null }] : []),
        ...(albums.data?.albums ?? [])
          // Only this circle's own albums take photos added here.
          .filter((x) => x.id !== from && !x.elsewhere)
          .map((x) => ({
            id: x.id,
            title: x.title,
            detail: plural(x.photos, 'photo'),
            cover: x.covers[0] ? albumPhotoUrl(x.covers[0], 'thumb') : null,
          })),
      ]}
      onPick={(id) => !send.isPending && send.mutate(id || null)}
      fresh={{ label: 'A new album', onSelect: onNewAlbum }}
    >
      <FormError error={send.error} />
    </PickOne>
  )
}

// Sending picked recipes you added to a book, the same way: a book, "No book" to take them out, or a new book.
export function SendRecipes({
  open,
  onOpenChange,
  familyId,
  recipes,
  from,
  inBook,
  onSent,
  onNewBook,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  familyId: string
  recipes: string[]
  from?: string
  inBook: boolean
  onSent: (bookId: string | null) => void
  onNewBook: () => void
}) {
  const queryClient = useQueryClient()
  const books = useBooks(familyId)
  const send = useMutation({
    mutationFn: async (bookId: string | null) => {
      await sendRecipes(familyId, recipes, bookId)
      return bookId
    },
    onSuccess: async (bookId) => {
      for (const key of ['books', 'book', 'recipe', 'cookbook', 'home']) {
        await queryClient.invalidateQueries({ queryKey: [key] })
      }
      onSent(bookId)
    },
  })
  const n = recipes.length
  return (
    <PickOne
      open={open}
      onOpenChange={onOpenChange}
      title={`Send ${n === 1 ? 'the recipe' : `${n} recipes`} to which book?`}
      description="Everyone who sees them now still will. The book adds the people it’s shared with."
      icon={BookOpen}
      choices={[
        ...(inBook ? [{ id: '', title: 'No book', detail: 'Take them out of their book', cover: null }] : []),
        ...(books.data?.books ?? [])
          // Only this circle's own books take recipes added here.
          .filter((x) => x.id !== from && !x.elsewhere)
          .map((x) => {
            const cover = x.covers?.[0] ?? x.cover
            return {
              id: x.id,
              title: x.title,
              detail: plural(x.recipes, 'recipe'),
              cover: cover ? photoUrl(cover, 'thumb') : null,
            }
          }),
      ]}
      onPick={(id) => !send.isPending && send.mutate(id || null)}
      fresh={{ label: 'A new book', onSelect: onNewBook }}
    >
      <FormError error={send.error} />
    </PickOne>
  )
}
